import type { QueryClient } from '@tanstack/react-query'
import type { Order } from '@/types'
import { invalidateTables } from '@/lib/tablesCache'
import { isOpenOrdersStripMember } from '@/components/counter/rail/ticketState'

/**
 * F-FE-N176 — one owner for refreshing the counter's Open Orders strip
 * (`['counterPendingOrders']`, GET /orders?open_only=true — the slowest read on
 * the till) and the table picker (`['tables']`).
 *
 * Why this exists: one desktop checkout on demo (2026-10-02 23:28 UTC) fetched
 * GET /orders 4–5 times around a single payment. Each surface asked for its own
 * refresh — the checkout-intent PATCH, the dashboard-stream `payment` event,
 * the `table_status_changed` event that lands in the same millisecond, and the
 * payment mutation's own onSuccess — and every `invalidateQueries` call defaults
 * to `cancelRefetch: true`, which abandons the in-flight request and starts
 * another. Our queryFns take no AbortSignal, so the abandoned request still
 * reaches the server: N invalidations in a burst = N full list reads.
 *
 * The refresher batches requests over a short window and fires ONE
 * invalidation per key at the end of it. Correctness is preserved by
 * construction: the fetch starts after every request in the window, so it sees
 * every server change that caused any of them.
 */

/** Long enough to merge one burst (stream events + mutation settle), short enough to be invisible. */
export const COUNTER_LIST_REFRESH_WINDOW_MS = 250

/** Mirrors useQuery({ queryKey: ['counterPendingOrders'] }) in CounterInterface. */
export const COUNTER_OPEN_ORDERS_KEY = ['counterPendingOrders'] as const

export interface CounterListRefreshRequest {
  /** Also refresh the table picker (+ admin tables summary). */
  tables?: boolean
}

export interface CounterListRefresher {
  /** Ask for the open-orders strip (and optionally tables) to be refreshed. Coalesced. */
  request(opts?: CounterListRefreshRequest): void
  /** Fire any pending refresh now. */
  flush(): void
  /**
   * Record a dashboard-stream `payment` event for an order. The stream handler
   * already requests a strip + tables refresh for it, after the server commit.
   */
  notePaymentEvent(orderId: string | undefined | null): void
  /**
   * True when a stream `payment` event for this order arrived at or after
   * `sinceMs` — i.e. the payment's own refresh is already covered and the
   * mutation's onSuccess must not ask again.
   */
  paymentRefreshCoveredSince(orderId: string, sinceMs: number): boolean
  /** Flush (never drop) pending work and stop the timer. */
  dispose(): void
}

export function createCounterListRefresher(
  qc: QueryClient,
  opts: { windowMs?: number; now?: () => number } = {},
): CounterListRefresher {
  const windowMs = opts.windowMs ?? COUNTER_LIST_REFRESH_WINDOW_MS
  const now = opts.now ?? (() => Date.now())
  let timer: ReturnType<typeof setTimeout> | null = null
  let pendingOrders = false
  let pendingTables = false
  const paymentEventAt = new Map<string, number>()

  const fire = () => {
    if (timer != null) {
      clearTimeout(timer)
      timer = null
    }
    const orders = pendingOrders
    const tables = pendingTables
    pendingOrders = false
    pendingTables = false
    if (tables) invalidateTables(qc)
    if (orders) qc.invalidateQueries({ queryKey: [...COUNTER_OPEN_ORDERS_KEY] })
  }

  return {
    request(req) {
      pendingOrders = true
      if (req?.tables) pendingTables = true
      // Fixed window from the FIRST request (not a resetting debounce): a
      // steady trickle of events can never postpone the refresh indefinitely.
      if (timer == null) timer = setTimeout(fire, windowMs)
    },
    flush: fire,
    notePaymentEvent(orderId) {
      if (!orderId) return
      paymentEventAt.set(orderId, now())
      // Bounded: only the last few orders matter (a payment settles in seconds).
      if (paymentEventAt.size > 50) {
        const oldest = paymentEventAt.keys().next().value
        if (oldest !== undefined) paymentEventAt.delete(oldest)
      }
    },
    paymentRefreshCoveredSince(orderId, sinceMs) {
      const at = paymentEventAt.get(orderId)
      return at != null && at >= sinceMs
    },
    dispose() {
      // Flush rather than drop: an invalidation with no mounted observer just
      // marks the cache stale, so the strip refetches when the counter remounts
      // instead of showing a pre-payment snapshot for up to staleTime (5 min).
      fire()
    },
  }
}

/**
 * Patch one order in the cached Open Orders strip with the copy the server just
 * returned (e.g. the checkout-intent PATCH response), instead of re-reading the
 * whole list. Returns false — and changes nothing — when the order is not in
 * the cache, so the caller can fall back to a real refresh.
 *
 * A row that the response shows as no longer open is dropped, matching the
 * filter the list query applies to fresh server data.
 */
export function patchOpenOrderInStrip(qc: QueryClient, order: Order): boolean {
  const prev = qc.getQueryData<Order[]>([...COUNTER_OPEN_ORDERS_KEY])
  if (!Array.isArray(prev)) return false
  const idx = prev.findIndex((o) => o.id === order.id)
  if (idx < 0) return false
  const next = prev.slice()
  next[idx] = { ...prev[idx], ...order }
  qc.setQueryData<Order[]>(
    [...COUNTER_OPEN_ORDERS_KEY],
    next.filter((o) => isOpenOrdersStripMember(o)),
  )
  return true
}
